<template>
  <div class="pay-page">
    <!-- Hero Section -->
    <section class="hero-section">
      <div class="container">
        <h1 class="hero-title">{{ $t('pay.hero.title') }}</h1>
        <p class="hero-subtitle">{{ $t('pay.hero.subtitle') }}</p>
      </div>
    </section>

    <!-- Main Content -->
    <div class="container main-content">
      <!-- Tab Navigation -->
      <div class="tabs-wrapper">
        <div class="tabs">
          <button
            class="tab-button"
            :class="{ 'is-active': activeTab === 'vip' }"
            @click="handleTabChange('vip')"
          >
            <Icon name="material-symbols:verified" />
            {{ $t('pay.tabs.vip') }}
          </button>
          <button
            class="tab-button"
            :class="{ 'is-active': activeTab === 'packages' }"
            @click="handleTabChange('packages')"
          >
            <Icon name="material-symbols:add-shopping-cart" />
            {{ $t('pay.tabs.packages') }}
          </button>
        </div>
      </div>

      <!-- Tab Content -->
      <div class="tab-content">
        <!-- Skeleton while loading or empty -->
        <PaySkeleton v-if="contentLoading" section="content" />

        <!-- Real content -->
        <template v-else>
          <!-- VIP Tab -->
          <div v-show="activeTab === 'vip'" class="tab-pane">
            <VipBenefits />

            <div class="section-title">{{ $t('pay.vip.selectPackage') }}</div>
            <PackageList
              :packages="payStore.vipPackages"
              :selected-id="payStore.selectedPackage?.productId || null"
              type="vip"
              @select="handlePackageSelect"
            />
          </div>

          <!-- Packages Tab -->
          <div v-show="activeTab === 'packages'" class="tab-pane">
            <ClientOnly>
              <template #default>
                <PagesStatus v-if="userStore.isLoggedIn" @buy-again="scrollToPackages" />
              </template>
            </ClientOnly>
            <PagesDescription />

            <div class="section-title">{{ $t('pay.packages.selectPackage') }}</div>
            <PackageList
              :packages="payStore.pagePackages"
              :selected-id="payStore.selectedPackage?.productId || null"
              type="page"
              @select="handlePackageSelect"
            />
          </div>
        </template>
      </div>

      <!-- Payment Section (CSR-only to avoid SSR/CSR hydration mismatch with login state) -->
      <ClientOnly>
        <template #default>
          <div v-if="userStore.isLoggedIn" class="payment-section">
            <PaymentMethods
              :methods="payStore.paymentMethods"
              :selected="payStore.selectedPaymentMethod"
              @change="handlePaymentMethodChange"
            />

            <PurchaseButton
              :price="payStore.displayPrice"
              :disabled="!payStore.canPurchase"
              :loading="payStore.isLoading"
              @click="handlePurchase"
            />
          </div>

          <!-- Guest CTA: 未登录用户不显示支付功能，仅提示登录 -->
          <div v-else class="payment-section guest">
            <div class="guest-cta">
              <Icon name="material-symbols:lock" size="20" />
              <span>{{ $t('pay.guest.prompt') }}</span>
              <button class="btn btn-primary" @click="openAuth('login')">{{ $t('pay.guest.login') }}</button>
            </div>
          </div>
        </template>
        <template #fallback>
          <!-- SSR 占位骨架，避免结构抖动 -->
          <div class="payment-section">
            <PaySkeleton section="payment" />
          </div>
        </template>
      </ClientOnly>
    </div>

    <section class="seo-rich-content">
      <div class="container">
        <h2 class="seo-title">{{ $t('pay.seoSection.title') }}</h2>
        <p class="seo-intro">{{ $t('pay.seoSection.intro') }}</p>

        <div class="seo-block">
          <h3 class="seo-subtitle">{{ $t('pay.seoSection.highlights.title') }}</h3>
          <ul class="seo-list">
            <li>{{ $t('pay.seoSection.highlights.items.0') }}</li>
            <li>{{ $t('pay.seoSection.highlights.items.1') }}</li>
            <li>{{ $t('pay.seoSection.highlights.items.2') }}</li>
          </ul>
        </div>

        <div class="seo-block">
          <h3 class="seo-subtitle">{{ $t('pay.seoSection.faq.title') }}</h3>
          <article class="faq-item">
            <h4>{{ $t('pay.seoSection.faq.items.0.q') }}</h4>
            <p>{{ $t('pay.seoSection.faq.items.0.a') }}</p>
          </article>
          <article class="faq-item">
            <h4>{{ $t('pay.seoSection.faq.items.1.q') }}</h4>
            <p>{{ $t('pay.seoSection.faq.items.1.a') }}</p>
          </article>
          <article class="faq-item">
            <h4>{{ $t('pay.seoSection.faq.items.2.q') }}</h4>
            <p>{{ $t('pay.seoSection.faq.items.2.a') }}</p>
          </article>
        </div>
      </div>
    </section>

    <!-- WeChat Pay Dialog -->
    <WxPayDialog
      v-model:visible="showWxDialog"
      :code-url="payStore.qrCodeUrl"
      :price="payStore.displayPrice"
      :order-id="payStore.currentOrderId"
      @close="handleDialogClose"
    />
  </div>
</template>

<script setup lang="ts">
import type { VipPackage, PagePackage } from '~/stores/pay'
import { useI18n } from 'vue-i18n'
import {
  FUNNEL_EVENTS,
  buildPayQrDedupeKey,
  buildPaySuccessDedupeKey,
  trackFunnelEvent
} from '~/utils/gaFunnelEvents.js'

const route = useRoute()
const router = useRouter()
const payStore = usePayStore()
const userStore = useUserStore()

const { t } = useI18n()

// 当前激活的Tab：在 setup 阶段根据路由初始化，避免 SSR/CSR 初始不一致
const initialTab = (route.query.tab as string) === 'packages' ? 'packages' : 'vip'
const activeTab = ref<'vip' | 'packages'>(initialTab)

// 数据加载/渲染占位判定
const hasVipData = computed(() => (payStore.vipPackages?.length || 0) > 0)
const hasPageData = computed(() => (payStore.pagePackages?.length || 0) > 0)
const contentLoading = computed(() => {
  if (payStore.isLoading) return true
  return activeTab.value === 'vip' ? !hasVipData.value : !hasPageData.value
})

// Wx Dialog visibility
const showWxDialog = computed({
  get: () => !!payStore.qrCodeUrl,
  set: (val) => {
    if (!val) {
      payStore.resetPaymentState()
    }
  }
})

// 页面初始化
onMounted(async () => {
  trackFunnelEvent(FUNNEL_EVENTS.PAY_PAGE_OPEN, {
    page_path: route.path
  })

  // 加载套餐数据
  await Promise.all([
    payStore.fetchVipPackages(),
    payStore.fetchPagePackages()
  ])
})

// 处理Tab切换
const handleTabChange = (tab: 'vip' | 'packages') => {
  activeTab.value = tab
  payStore.clearSelection()

  // 根据Tab重新选中默认套餐
  if (tab === 'vip' && payStore.vipPackages.length > 0) {
    const selected = payStore.vipPackages[0]
    if (selected) payStore.setSelectedPackage(selected)
  } else if (tab === 'packages' && payStore.pagePackages.length > 0) {
    const selected = payStore.pagePackages[0]
    if (selected) payStore.setSelectedPackage(selected)
  }
}

// 处理套餐选择
const handlePackageSelect = (pkg: VipPackage | PagePackage) => {
  payStore.setSelectedPackage(pkg)
}

// 处理支付方式变更
const handlePaymentMethodChange = (method: any) => {
  payStore.setPaymentMethod(method)
}

// 滚动到套餐区域
const scrollToPackages = () => {
  const packageList = document.querySelector('.package-list')
  if (packageList) {
    packageList.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
}

// 处理购买
const handlePurchase = async () => {
  if (!payStore.selectedPackage) {
    ElMessage.warning(t('pay.payment.selectFirst'))
    return
  }

  try {
    await payStore.createOrder(
      payStore.selectedPackage,
      payStore.selectedPaymentMethod
    )
    // 注意：微信支付此时会设置 qrCodeUrl，触发 showWxDialog 变为 true
  } catch (error: any) {
    console.error('Purchase failed:', error)
    // 支付失败通常抛出 Error 对象，显示 message
    ElMessage.error(error.message || t('pay.errors.purchaseFailed'))
  }
}

const handleDialogClose = () => {
  payStore.resetPaymentState()
}

watch(() => payStore.qrCodeUrl, (qrCodeUrl) => {
  if (!qrCodeUrl) return
  const orderId = payStore.currentOrderId
  trackFunnelEvent(
    FUNNEL_EVENTS.PAY_QR_SHOW,
    { order_id: orderId || undefined },
    { dedupeKey: buildPayQrDedupeKey(orderId) }
  )
})

// 监听支付状态
watch(() => payStore.paymentStatus, (status) => {
  if (status === 'paid') {
    const orderId = payStore.currentOrderId
    trackFunnelEvent(
      FUNNEL_EVENTS.PAY_SUCCESS,
      { order_id: orderId || undefined },
      { dedupeKey: buildPaySuccessDedupeKey(orderId) }
    )
    ElMessage.success(t('pay.payment.success'))
    payStore.resetPaymentState() // 关闭弹窗
    // 路由跳转由 store.onPaySuccess 处理
  } else if (status === 'expired') {
    ElMessage.warning(t('pay.payment.expired'))
    // 可以选择是否关闭弹窗，这里保持打开让用户看到过期提示或者手动关闭
  } else if (status === 'fail') {
    ElMessage.error(t('common.actionFailed'))
    payStore.resetPaymentState()
  }
})

// SEO Meta
useWebsiteSeo({
  title: () => t('seo.pages.pay.title'),
  description: () => t('seo.pages.pay.desc'),
  forceNoIndex: true,
  includeAlternates: false
})

// 打开登录弹窗
const openAuth = (_mode: 'login' | 'register') => {
  userStore.setAuthModalVisible(true)
}
</script>

<style scoped lang="scss">
.pay-page {
  min-height: 100vh;
  background: #f8f9fa;
}

.hero-section {
  background: linear-gradient(135deg, #4A90E2 0%, #50E3C2 100%);
  padding: 60px 20px;
  text-align: center;
  color: white;

  @media (max-width: 768px) {
    padding: 40px 20px;
  }
}

.hero-title {
  font-size: 36px;
  font-weight: 700;
  margin: 0 0 12px 0;

  @media (max-width: 768px) {
    font-size: 28px;
  }
}

.hero-subtitle {
  font-size: 18px;
  opacity: 0.95;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 16px;
  }
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.main-content {
  padding: 40px 20px;
}

.seo-rich-content {
  background: #ffffff;
  border-top: 1px solid #e5e7eb;
  margin-top: 16px;
  padding: 40px 0 56px;
}

.seo-title {
  font-size: 28px;
  line-height: 1.3;
  color: #111827;
  margin: 0 0 16px 0;
}

.seo-intro {
  font-size: 16px;
  line-height: 1.75;
  color: #374151;
  margin: 0 0 20px 0;
}

.seo-block {
  margin-top: 24px;
}

.seo-subtitle {
  font-size: 20px;
  line-height: 1.4;
  color: #111827;
  margin: 0 0 12px 0;
}

.seo-list {
  margin: 0;
  padding-left: 20px;
  color: #374151;

  li {
    margin-bottom: 8px;
    line-height: 1.75;
  }
}

.faq-item {
  padding: 14px 0;
  border-bottom: 1px dashed #d1d5db;

  &:last-child {
    border-bottom: none;
  }

  h4 {
    margin: 0 0 6px 0;
    font-size: 17px;
    color: #111827;
    line-height: 1.45;
  }

  p {
    margin: 0;
    color: #4b5563;
    line-height: 1.75;
  }
}

.tabs-wrapper {
  margin-bottom: 40px;
}

.tabs {
  display: flex;
  justify-content: center;
  gap: 16px;
  border-bottom: 2px solid #e5e7eb;
  padding-bottom: 0;

  @media (max-width: 768px) {
    gap: 8px;
  }
}

.tab-button {
  flex: 1;
  max-width: 240px;
  padding: 16px 24px;
  background: transparent;
  border: none;
  border-bottom: 3px solid transparent;
  font-size: 16px;
  font-weight: 600;
  color: #6C757D;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  position: relative;
  bottom: -2px;

  &:hover {
    color: #4A90E2;
  }

  &.is-active {
    color: #4A90E2;
    border-bottom-color: #4A90E2;
  }

  svg {
    font-size: 20px;
  }

  @media (max-width: 768px) {
    max-width: none;
    padding: 14px 16px;
    font-size: 15px;
  }
}

.tab-content {
  margin-bottom: 40px;
}

.tab-pane {
  animation: fadeIn 0.3s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
}

.section-title {
  font-size: 20px;
  font-weight: 600;
  color: #212529;
  margin: 32px 0 20px 0;
}

.payment-section {
  width: 100%;
  padding: 32px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);

  @media (max-width: 768px) {
    padding: 24px;
  }
}

.payment-section.guest {
  .guest-cta {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
  }
  .btn.btn-primary {
    padding: 8px 14px;
    border-radius: 8px;
    background: $primary-color;
    color: #fff;
    border: 1px solid $primary-color;
    cursor: pointer;
  }
}

@media (max-width: 768px) {
  .main-content {
    padding: 24px 16px;
  }

  .seo-rich-content {
    padding: 28px 0 36px;
  }

  .seo-title {
    font-size: 22px;
  }

  .seo-subtitle {
    font-size: 18px;
  }

  .seo-intro,
  .seo-list li,
  .faq-item p {
    font-size: 14px;
  }

  .faq-item h4 {
    font-size: 16px;
  }
}
</style>
